import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import api from "@/utils/axios";
import cls from "./MemberProfileEditPaymentsSingle.module.scss";

const MemberProfileEditPaymentsSingle = () => {
  const { t } = useTranslation(); // using common/en.json
  const { memberPaymentSubmissionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchSubmission = async () => {
      try {
        setLoading(true);
        setError("");

        if (!memberPaymentSubmissionId) {
          throw new Error(
            t("memberProfilePayments.errors.missingSubmissionId"),
          );
        }

        const res = await api.get(
          `/v1/member/profile/payment-submissions/${memberPaymentSubmissionId}`,
        );

        if (!isMounted) return;
        setSubmission(res.data || null);
      } catch (err) {
        if (!isMounted) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          t("memberProfilePayments.errors.genericLoadError");
        setError(msg);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSubmission();

    return () => {
      isMounted = false;
    };
  }, [memberPaymentSubmissionId, t]);

  const handleBack = () => {
    // You can change this to "/my-profile/edit/payments"
    // if you have a list page for submissions
    navigate("/lounge/my-profile/edit");
  };

  const formatDate = (value) => {
    if (!value) return t("memberProfilePayments.common.noValue");
    const d = new Date(value);
    if (Number.isNaN(d.getTime()))
      return t("memberProfilePayments.common.noValue");
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const formatDateTime = (value) => {
    if (!value) return t("memberProfilePayments.common.noValue");
    const d = new Date(value);
    if (Number.isNaN(d.getTime()))
      return t("memberProfilePayments.common.noValue");
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderStatusLabel = (status) => {
    if (status === "approved") {
      return t("memberProfilePayments.status.approved");
    }
    if (status === "decline" || status === "rejected") {
      return t("memberProfilePayments.status.declined");
    }
    return t("memberProfilePayments.status.pending");
  };

  const statusClass = (status) => {
    if (status === "approved") return cls.statusApproved;
    if (status === "decline" || status === "rejected")
      return cls.statusDeclined;
    return cls.statusPending;
  };

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          <span className={cls.backIcon} aria-hidden="true">
            ←
          </span>
          <span>{t("memberProfilePayments.actions.backToProfile")}</span>
        </button>

        <div className={cls.headerText}>
          <h1 className={cls.title}>{t("memberProfilePayments.title")}</h1>
          <p className={cls.subtitle}>{t("memberProfilePayments.subtitle")}</p>
        </div>
      </header>

      {loading && (
        <div className={cls.stateBox}>
          <div className={cls.loader} />
          <span>{t("memberProfilePayments.states.loading")}</span>
        </div>
      )}

      {!loading && error && (
        <div className={cls.stateBoxError}>
          <span className={cls.stateLabel}>
            {t("memberProfilePayments.states.errorLabel")}
          </span>
          <p className={cls.stateMessage}>{error}</p>
        </div>
      )}

      {!loading && !error && !submission && (
        <div className={cls.stateBoxEmpty}>
          <h2 className={cls.emptyTitle}>
            {t("memberProfilePayments.states.notFoundTitle")}
          </h2>
          <p className={cls.emptyText}>
            {t("memberProfilePayments.states.notFoundText")}
          </p>
        </div>
      )}

      {!loading && !error && submission && (
        <Motion.section
          className={cls.card}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Top row: method + status */}
          <div className={cls.cardTop}>
            <div className={cls.cardTitleGroup}>
              <h2 className={cls.cardTitle}>
                {submission.paymentOption?.title ||
                  t("memberProfilePayments.details.unknownMethod")}
              </h2>
              <p className={cls.cardSubtitle}>
                {t("memberProfilePayments.details.submissionIdLabel")}{" "}
                <span className={cls.cardId}>{submission.id}</span>
              </p>
            </div>

            <span
              className={`${cls.statusBadge} ${statusClass(submission.status)}`}
            >
              {renderStatusLabel(submission.status)}
            </span>
          </div>

          {/* Info rows */}
          <div className={cls.infoGrid}>
            <div className={cls.infoItem}>
              <span className={cls.infoLabel}>
                {t("memberProfilePayments.details.transferDate")}
              </span>
              <span className={cls.infoValue}>
                {formatDate(submission.transferDate)}
              </span>
            </div>

            <div className={cls.infoItem}>
              <span className={cls.infoLabel}>
                {t("memberProfilePayments.details.submittedOn")}
              </span>
              <span className={cls.infoValue}>
                {formatDateTime(submission.createdAt)}
              </span>
            </div>

            <div className={cls.infoItem}>
              <span className={cls.infoLabel}>
                {t("memberProfilePayments.details.lastUpdated")}
              </span>
              <span className={cls.infoValue}>
                {formatDateTime(submission.updatedAt)}
              </span>
            </div>

            <div className={cls.infoItem}>
              <span className={cls.infoLabel}>
                {t("memberProfilePayments.details.approvedAt")}
              </span>
              <span className={cls.infoValue}>
                {submission.approvedAt
                  ? formatDateTime(submission.approvedAt)
                  : t("memberProfilePayments.common.notYet")}
              </span>
            </div>
          </div>

          {/* Payment option description / instructions */}
          {submission.paymentOption && (
            <div className={cls.optionBox}>
              <h3 className={cls.optionTitle}>
                {t("memberProfilePayments.details.paymentMethodHeading")}
              </h3>
              {submission.paymentOption.description && (
                <p className={cls.optionText}>
                  {submission.paymentOption.description}
                </p>
              )}
              {submission.paymentOption.instruction && (
                <p className={cls.optionInstruction}>
                  {submission.paymentOption.instruction}
                </p>
              )}
            </div>
          )}

          {/* Proof image */}
          <div className={cls.proofBox}>
            <h3 className={cls.proofTitle}>
              {t("memberProfilePayments.details.proofHeading")}
            </h3>

            {submission.proofImage?.url ? (
              <figure className={cls.proofFigure}>
                <img
                  src={submission.proofImage.url}
                  alt={t("memberProfilePayments.details.proofAlt")}
                  className={cls.proofImage}
                />
                <figcaption className={cls.proofCaption}>
                  {t("memberProfilePayments.details.proofCaption")}
                </figcaption>
              </figure>
            ) : (
              <p className={cls.proofPlaceholder}>
                {t("memberProfilePayments.details.noProofImage")}
              </p>
            )}
          </div>

          {/* Helper note */}
          <div className={cls.helperBox}>
            <p className={cls.helperText}>
              {t("memberProfilePayments.details.helperNote")}
            </p>
          </div>
        </Motion.section>
      )}
    </div>
  );
};

export default MemberProfileEditPaymentsSingle;
